import axios, { extractData, miscApi } from '@atheneum/api'
import { formatQuarter } from '@atheneum/api/dist/lib'
import { computed, extendObservable } from 'mobx'
import moment from 'moment'

class Dashboard {
  constructor (staticData, userStore) {
    extendObservable(this, {
      staticData,
      userStore,
      loading: false,
      error: null,
      status: null,
      expertOverview: null,
      expertPaymentStatus: null,
      expiringContracts: null,
      recentClientRecords: null,
      bookmarkedProjects: null,
      performanceWidgetData: null,
      teamRevenueWidgetData: null,
      accountRevenueWidgetData: null,
      accountRevenueQuarterlyWidgetData: null,
      honorariaAnalysisWidgetData: null,
      principalTeamsData: null,
      mySegments: null,
      extensionInfo: null,
      performanceMetrics: computed(() => {
        if (!this.performanceWidgetData) return null
        return this.formMetricsData(this.performanceWidgetData.metrics)
      }),
      expertPaymentStatusMetrics: computed(() => {
        if (!this.expertPaymentStatus) return null
        return this.preparePaymentStatusMetrics(this.expertPaymentStatus)
      }),
      expertOverviewMetrics: computed(() => {
        if (!this.expertOverview) return null
        return this.prepareOverviewMetrics(this.expertOverview)
      })
    })
  }

  getFilteredFilteredSubTeams = (
    subTeams = this.teamRevenueWidgetData.subTeams
  ) => subTeams.filter(subTeam => subTeam.length)

  getTotalTeamMembersValues = (
    teamMembers = this.teamRevenueWidgetData.teamMembers
  ) => {
    const total = teamMembers
      ? teamMembers.reduce(
          (acc, teamMember) => {
            acc.revenue += teamMember.revenue
            acc.cost += teamMember.cost

            const revenue = teamMember.revenue || 0
            const cost = teamMember.cost || 0
            acc.grossMargin += (revenue - cost) / 1e6
            acc.targetMargin += teamMember.targetMargin
            acc.interviewCount += teamMember.interviewCount
            acc.employeeDeliveries += teamMember.employeeDeliveries
            return acc
          },
          {
            revenue: 0,
            cost: 0,
            grossMargin: 0,
            targetMargin: 0,
            interviewCount: 0,
            employeeDeliveries: 0
          }
        )
      : 0

    total.achievement =
      total.grossMargin && total.targetMargin
        ? (total.grossMargin / total.targetMargin) * 100
        : 0

    return total
  }

  getAccountRevenueQuarterlyTotals = (
    months = this.accountRevenueQuarterlyWidgetData.months
  ) => {
    const totals = months.reduce(
      (acc, month) => {
        acc.revenue += Number.isInteger(month.revenue)
          ? parseFloat((month.revenue / 1e6).toFixed(2))
          : 0
        acc.target =
          month.revenue && Number.isInteger(month.revenue)
            ? month.target
            : acc.target
        return acc
      },
      {
        revenue: 0,
        target: 0
      }
    )

    totals.achievement =
      totals.target && totals.revenue
        ? (totals.revenue / totals.target) * 100
        : 0

    return totals
  }

  getAccountRevenueMonthlyTotals = (
    accounts = this.accountRevenueWidgetData.accounts
  ) => {
    return accounts.reduce(
      (acc, account) => {
        acc.ES += Number.isInteger(account.ES)
          ? parseFloat((account.ES / 1e6).toFixed(2))
          : 0
        acc.EBR += Number.isInteger(account.EBR)
          ? parseFloat((account.EBR / 1e6).toFixed(2))
          : 0
        acc.ESR += Number.isInteger(account.ESR)
          ? parseFloat((account.ESR / 1e6).toFixed(2))
          : 0
        acc.EP += Number.isInteger(account.EP)
          ? parseFloat((account.EP / 1e6).toFixed(2))
          : 0
        return acc
      },
      {
        ES: 0,
        EBR: 0,
        ESR: 0,
        EP: 0
      }
    )
  }

  getFormattedTeamMembers = (
    teamMembers = this.teamRevenueWidgetData.teamMembers
  ) =>
    teamMembers
      ? teamMembers.map(teamMember => {
          const revenue = teamMember.revenue || 0
          const cost = teamMember.cost || 0
          teamMember.grossMargin = (revenue - cost) / 1e6
          teamMember.achievement =
            teamMember.grossMargin && teamMember.targetMargin
              ? (teamMember.grossMargin / teamMember.targetMargin) * 100
              : 0

          return teamMember
        })
      : []

  getSortedTeamMembers = teamMembers =>
    teamMembers
      ? teamMembers.sort((a, b) => {
          if (a.position === 'Team Leader') {
            return -1
          }

          if (b.position === 'Team Leader') {
            return 1
          }

          if (a.position === 'Associate Intern') {
            return 1
          }

          if (b.position === 'Associate Intern') {
            return -1
          }

          if (a.position === 'Associate') {
            return 1
          }

          if (b.position === 'Associate') {
            return -1
          }
          return 0
        })
      : []

  getData = () => {
    this.loading = true

    Promise.all([
      this.getExpertsOverview(),
      this.getExpertPaymentStatus(),
      this.getExpiringContracts(),
      this.getRecentClientRecords(),
      this.getBookmarkedProjects(),
      this.getMySegmentsListData(),
      this.getExtensionInfo(),
      this.getPrincipalTeamsData()
    ])
      .then(responses => {
        this.loading = false
      })
      .catch(err => {
        this.error = err
      })
  }

  // Widget data endpoints

  getExpertsOverview = () => {
    return axios.get('/api/report/experts-overview').then(expertOverview => {
      const expertOverviewData = extractData(expertOverview)
      this.expertOverview = expertOverviewData
    })
  }

  getExpertPaymentStatus = () => {
    if (!this.userStore.isAdvancedReporting() && !this.userStore.isAdmin()) {
      return
    }

    return axios
      .get('/api/consultation-payment-detail/widget-data')
      .then(extractData)
      .then(expertPaymentStatus => {
        this.expertPaymentStatus = expertPaymentStatus
      })
      .catch(error => {
        this.error = error
      })
  }

  getExpiringContracts = () => {
    if (!this.userStore.isAdvancedReporting() && !this.userStore.isAdmin()) {
      return
    }

    return axios
      .get('/api/report/expiring-contracts')
      .then(extractData)
      .then(expiringContracts => {
        this.expiringContracts = expiringContracts
      })
      .catch(error => {
        this.error = error
      })
  }

  getRecentClientRecords = () => {
    if (!this.userStore.isAdvancedReporting() && !this.userStore.isAdmin()) {
      return
    }

    return axios
      .get('/api/report/recent-client-records')
      .then(extractData)
      .then(recentClientRecords => {
        this.recentClientRecords = recentClientRecords
      })
      .catch(error => {
        this.error = error
      })
  }

  getBookmarkedProjects = () => {
    return axios
      .get('/api/project/bookmarked')
      .then(extractData)
      .then(bookmarkedProjects => {
        this.bookmarkedProjects = bookmarkedProjects
      })
      .catch(error => {
        this.error = error
      })
  }

  getPerformanceWidgetData = (month, teamId = null) => {
    return axios
      .get(
        `/api/report/performance-widget-data?month=${month}` +
          (teamId ? `&teamId=${teamId}` : '')
      )
      .then(extractData)
      .then(performanceWidgetData => {
        this.performanceWidgetData = performanceWidgetData
      })
      .catch(error => {
        this.error = error
      })
  }

  getTeamRevenueWidgetData = (month, teamId = null) => {
    return axios
      .get(
        `/api/report/team-revenue-widget-data?month=${month}` +
          (teamId ? `&teamId=${teamId}` : '')
      )
      .then(extractData)
      .then(teamRevenueWidgetData => {
        this.teamRevenueWidgetData = teamRevenueWidgetData
      })
      .catch(error => {
        this.error = error
      })
  }

  getPrincipalTeamsData = () => {
    return axios
      .get('/api/report/principal-teams-widget')
      .then(extractData)
      .then(principalTeamsData => {
        const date = moment().format('MM-YYYY')
        this.principalTeamsData = principalTeamsData
        Promise.all([
          this.getPerformanceWidgetData(
            date,
            this.principalTeamsData[0] ? this.principalTeamsData[0].id : null
          ),
          this.getTeamRevenueWidgetData(
            date,
            this.principalTeamsData[0] ? this.principalTeamsData[0].id : null
          ),
          this.getAccountRevenueWidgetData(
            date,
            this.principalTeamsData[0] ? this.principalTeamsData[0].id : null
          ),
          this.getAccountRevenueQuarterlyWidgetData({
            quarter: formatQuarter(),
            year: new Date().getFullYear(),
            teamId: this.principalTeamsData[0]
              ? this.principalTeamsData[0].id
              : null
          }),
          this.getHonorariaAnalysisWidgetData(
            date,
            this.principalTeamsData[0] ? this.principalTeamsData[0].id : null
          )
        ])
      })
      .catch(error => {
        this.error = error
      })
  }

  getAccountRevenueWidgetData = (month, teamId = null) => {
    return axios
      .get(
        `/api/report/account-revenue-widget-data?month=${month}` +
          (teamId ? `&teamId=${teamId}` : '')
      )
      .then(extractData)
      .then(accountRevenueWidgetData => {
        this.accountRevenueWidgetData = accountRevenueWidgetData
      })
      .catch(error => {
        this.error = error
      })
  }

  getAccountRevenueQuarterlyWidgetData = ({ quarter, year, teamId = null }) => {
    return axios
      .get(
        `/api/report/account-revenue-quarterly-widget-data?year=${year}&quarter=${quarter}` +
          (teamId ? `&teamId=${teamId}` : '')
      )
      .then(extractData)
      .then(accountRevenueQuarterlyWidgetData => {
        this.accountRevenueQuarterlyWidgetData = accountRevenueQuarterlyWidgetData
      })
      .catch(error => {
        this.error = error
      })
  }

  getHonorariaAnalysisWidgetData = (month, teamId = null) => {
    return axios
      .get(
        `/api/report/honoraria-analysis-widget-data?month=${month}` +
          (teamId ? `&teamId=${teamId}` : '')
      )
      .then(extractData)
      .then(honorariaAnalysisWidgetData => {
        this.honorariaAnalysisWidgetData = honorariaAnalysisWidgetData
      })
      .catch(error => {
        this.error = error
      })
  }

  getMySegmentsListData = () => {
    return axios
      .get('/api/segment')
      .then(extractData)
      .then(segmentData => {
        this.mySegments = segmentData
      })
      .catch(error => {
        this.error = error
      })
  }

  getExtensionInfo = () => {
    return miscApi.getExtensionInfo().then(info => {
      this.extensionInfo = info
    })
  }

  // data helpers
  formMetricsData = metrics => {
    return [
      {
        id: 1,
        label: '1st delivery',
        value: metrics.deliveryFirst,
        tooltip:
          'Elapsed time in hours since 9:00 a.m. today until the moment your first Expert was submitted to Client'
      },
      {
        id: 2,
        label: 'Last delivery',
        value: metrics.deliveryLast,
        tooltip:
          'Elapsed time in hours since the moment your last Expert was submitted to Client'
      },
      {
        id: 3,
        label: 'Avg delivery',
        value: metrics.deliveryAverage,
        tooltip:
          'The average number of your delivered Experts (submitted to Client) per hour in this month'
      },
      {
        id: 4,
        label: '1st interview',
        value: metrics.deliveryFirst,
        tooltip: `Elapsed time in hours since 9:00 a.m. today until the moment your first Expert was marked to 'Interviewed'`
      },
      {
        id: 5,
        label: 'Last interview',
        value: metrics.interviewLast,
        tooltip: `Elapsed time in hours since the moment your last Expert was marked to 'Interviewed'`
      },
      {
        id: 6,
        label: 'Avg interview',
        value: metrics.interviewAverage,
        tooltip:
          'The average number of your Interviewed Experts per hour in this month'
      }
    ]
  }

  preparePaymentStatusMetrics = expertPaymentStatus => {
    return [
      { id: 1, label: 'Need review', value: expertPaymentStatus.needReview },
      {
        id: 2,
        label: 'Pending payment',
        value: expertPaymentStatus.pendingPayment
      },
      {
        id: 3,
        label: 'Paid this month',
        value: expertPaymentStatus.paidThisMonth
      }
    ]
  }

  prepareOverviewMetrics = expertOverview => {
    return [
      {
        id: 1,
        label: 'Total',
        value: expertOverview.totalCount
      },
      {
        id: 2,
        label: 'Active',
        value: expertOverview.activeCount
      },
      {
        id: 3,
        label: 'New this month',
        value: expertOverview.newCount
      }
    ]
  }
}

export default Dashboard
